/**
 * What a reconstruction is doing, what it produced, and what to do about it.
 *
 * One module, because this screen used to answer those three questions from
 * three different places and they disagreed. The header badge read the job
 * status, an eight-step strip re-derived a second pipeline story from the same
 * status, and the task centre in the shell told a third version - so a run in
 * the middle of meshing could simultaneously read "İşleniyor", "Seyrek 3D:
 * İşleniyor" and "Seyrek rekonstrüksiyon çalışıyor".
 *
 * Three inputs and no others: the job's `status`, the `current_phase` the
 * worker reported, and the `model_state` the server derived from validated
 * artifacts. Nothing here guesses a phase, invents a percentage, or reads an
 * artifact list to decide whether a model exists.
 */

import type {
  ModelState,
  ReconstructionCapability,
  ReconstructionJob,
  ReconstructionPhase,
} from "@/api/types";
import type { StatusTone } from "@/i18n/terms";

/* --- what this installation can do ---------------------------------------- */

/**
 * Four answers, and only one of them is "nothing is connected".
 *
 * A capability request that has not come back, or that failed, says nothing
 * about whether a GPU worker exists. Collapsing it into `unavailable` is how
 * the surface controls vanished for anyone whose first request was slow, with
 * no explanation and nothing to wait for. `unknown` fixed that.
 *
 * `no_gpu` fixes the other half of the same mistake, and it was live: a
 * connected worker running a CPU build of COLMAP reports `dense: false`, this
 * function returned `unavailable`, and the screen said "3D işleme sunucusu şu
 * anda bağlı değil" to a customer whose server was connected and had just
 * finished a sparse reconstruction on it. The customer was told to wait for a
 * connection they already had.
 *
 * The two facts are separate in the contract - `worker_online` beside the
 * stage flags - so they are separate here. Nothing derives one from the other.
 */
export type SurfaceAvailability = "available" | "offline" | "no_gpu" | "unknown";

export function surfaceAvailability(
  capability: ReconstructionCapability | null,
  { loading }: { loading: boolean },
): SurfaceAvailability {
  // Loading and failed are the same answer on purpose: neither is evidence.
  if (!capability) return loading ? "unknown" : "unknown";
  if (capability.dense && capability.mesh) return "available";
  // `worker_online` is optional on the wire so an older API - or a cached
  // response written before the field existed - does not turn every
  // deployment into "offline". Absent means the field is not being reported,
  // and the honest reading of a missing fact is the one that claims least:
  // the machine is there and cannot do this stage.
  return capability.worker_online === false ? "offline" : "no_gpu";
}

/* --- the two things a customer can start ---------------------------------- */

/**
 * The surface options, named by what the customer chooses rather than by the
 * profile behind them.
 *
 * The differences are real and measured: all three run the *same* dense
 * settings, and differ only in how many photographs they may use and therefore
 * how long PatchMatch runs. So the copy talks about breadth and time, and
 * never about quality - nothing here has measured that more photographs
 * produce a better model.
 */
export interface SurfaceOption {
  images: number;
  /** The profile name. Sent to the API, never shown. */
  profile: string;
  label: string;
  description: string;
}

export const SURFACE_OPTIONS: readonly SurfaceOption[] = [
  {
    images: 60,
    profile: "rtx5080_mesh_v1",
    label: "60 fotoğraf",
    description: "Dengeli seçenek. Çoğu çekim için önerilen başlangıç.",
  },
  {
    images: 100,
    profile: "rtx5080_mesh_100_v1",
    label: "100 fotoğraf",
    description: "Daha geniş çekimlerde daha fazla görüntü kullanır; işlem daha uzun sürer.",
  },
  {
    images: 200,
    profile: "rtx5080_mesh_200_v1",
    label: "200 fotoğraf",
    description: "Çok büyük çekim setleri için. İşlem belirgin şekilde daha uzun sürer.",
  },
] as const;

/** The measured, balanced choice. Nobody is steered to 200 by default. */
export const DEFAULT_SURFACE_OPTION = SURFACE_OPTIONS[0]!;

export const isSurfaceProfile = (profile: string): boolean =>
  SURFACE_OPTIONS.some((option) => option.profile === profile);

/* --- progress -------------------------------------------------------------- */

/**
 * The customer's steps, and the real phases behind each one.
 *
 * Four rather than eight, and each is a thing the *user* recognises. The
 * worker's own phases are kept underneath so the technical detail is still
 * available and still true - the mapping is the only place the two vocabularies
 * meet.
 *
 * `input_transfer` and `output_transfer` are folded into the steps around them
 * rather than given steps of their own: moving bytes is real time but it is not
 * a thing a customer decides anything about.
 */
export interface CustomerStep {
  key: string;
  label: string;
  phases: readonly ReconstructionPhase[];
}

export const SPARSE_STEPS: readonly CustomerStep[] = [
  { key: "prepare", label: "Fotoğraflar hazırlanıyor", phases: ["input_transfer"] },
  { key: "cameras", label: "Kamera konumları belirleniyor", phases: ["sparse"] },
  { key: "deliver", label: "Sonuç kaydediliyor", phases: ["output_transfer"] },
] as const;

export const SURFACE_STEPS: readonly CustomerStep[] = [
  { key: "prepare", label: "Fotoğraflar hazırlanıyor", phases: ["input_transfer"] },
  { key: "cameras", label: "Kamera konumları belirleniyor", phases: ["sparse"] },
  { key: "detail", label: "3D yapı ayrıntılandırılıyor", phases: ["dense"] },
  { key: "surface", label: "Yüzey oluşturuluyor", phases: ["mesh", "glb"] },
  // Its own step rather than folded into the surface, because a customer
  // watching a bar that says "Yüzey oluşturuluyor" for the whole of a
  // texturing run has been told the wrong thing about where the time is
  // going - the same reasoning that gave the transfers their own phases.
  { key: "texture", label: "Doku hazırlanıyor", phases: ["texture"] },
  { key: "deliver", label: "Sonuç kaydediliyor", phases: ["output_transfer"] },
] as const;

export function stepsFor(job: ReconstructionJob | null): readonly CustomerStep[] {
  return job && isSurfaceProfile(job.profile_name) ? SURFACE_STEPS : SPARSE_STEPS;
}

export type StepState = "done" | "current" | "waiting" | "failed" | "unknown";

export interface StepView {
  step: CustomerStep;
  state: StepState;
}

/**
 * Where the run has got to, derived from the reported phase alone.
 *
 * A running job with no phase yet leaves every step `unknown` rather than
 * marking the first one done: "the worker has not said" is a real answer and
 * the screen says it in words instead of drawing a guess.
 *
 * A failed job marks the step its phase was in as failed and leaves the rest
 * untouched. The phase is where it *was*, which is the honest thing to show;
 * nothing here re-derives a stage from the failure code.
 */
export function stepViews(job: ReconstructionJob | null): StepView[] {
  const steps = stepsFor(job);
  if (!job || job.status === "queued") {
    return steps.map((step) => ({ step, state: "waiting" as StepState }));
  }
  if (job.status === "succeeded") {
    return steps.map((step) => ({ step, state: "done" as StepState }));
  }

  const phase = job.current_phase ?? null;
  const index = phase ? steps.findIndex((step) => step.phases.includes(phase)) : -1;
  if (index === -1) {
    // Running or failed, with no phase this build can place. Not a guess.
    return steps.map((step) => ({ step, state: "unknown" as StepState }));
  }
  return steps.map((step, position) => ({
    step,
    state:
      position < index
        ? "done"
        : position > index
          ? "waiting"
          : job.status === "failed"
            ? "failed"
            : "current",
  }));
}

export const STEP_TONES: Record<StepState, StatusTone> = {
  done: "success",
  current: "neutral",
  waiting: "neutral",
  failed: "danger",
  unknown: "neutral",
};

/* --- elapsed --------------------------------------------------------------- */

export interface Elapsed {
  /** Whole seconds since the run entered its current wait. */
  seconds: number;
  /** Which wait: queued behind other work, or actually being processed. */
  kind: "queued" | "running" | "finished";
}

/**
 * How long this has been going, from the server's own timestamps.
 *
 * Never from when the component mounted: a page reload would restart the
 * number, and a run somebody left and came back to would read as though it had
 * just begun. `finished_at` freezes it, so a completed run stops counting.
 */
export function elapsedOf(job: ReconstructionJob, now: number): Elapsed | null {
  const at = (value: string | null | undefined): number | null => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const started = at(job.started_at);
  const finished = at(job.finished_at);
  const queued = at(job.queued_at) ?? at(job.created_at);

  if (started !== null && finished !== null) {
    return { seconds: Math.max(0, Math.round((finished - started) / 1000)), kind: "finished" };
  }
  if (job.status === "running" && started !== null) {
    return { seconds: Math.max(0, Math.round((now - started) / 1000)), kind: "running" };
  }
  if (job.status === "queued" && queued !== null) {
    return { seconds: Math.max(0, Math.round((now - queued) / 1000)), kind: "queued" };
  }
  return null;
}

/** `2 sa 05 dk`, `12 dk 34 sn`, `48 sn`. Never a fabricated zero. */
export function formatElapsed(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = whole % 60;
  if (hours > 0) return `${hours} sa ${String(minutes).padStart(2, "0")} dk`;
  if (minutes > 0) return `${minutes} dk ${String(rest).padStart(2, "0")} sn`;
  return `${rest} sn`;
}

export function elapsedText(elapsed: Elapsed): string {
  const duration = formatElapsed(elapsed.seconds);
  if (elapsed.kind === "queued") return `${duration}dir sırada`;
  if (elapsed.kind === "running") return `${duration}dir işleniyor`;
  return `${duration} sürdü`;
}

/* --- what came out of it --------------------------------------------------- */

export type OutcomeKey = "model" | "dense" | "sparse" | "none" | "unknown";

export interface Outcome {
  key: OutcomeKey;
  headline: string;
  detail: string;
  tone: StatusTone;
}

/**
 * What a finished run actually produced.
 *
 * Read from `model_state`, which the server derives from artifacts it
 * validated - never from the job status and never from the artifact list here.
 * `has_model` is the only thing that licenses "3D modeliniz hazır"; a
 * sparse-only run succeeded perfectly well and contains no surface, and saying
 * otherwise is the single most misleading sentence this screen could print.
 */
export function outcomeOf(model: ModelState | null | undefined): Outcome {
  if (!model) {
    // A server that did not send one, or one older than this field. Not a
    // claim in either direction: absence is the third answer here as
    // everywhere else in this product.
    return {
      key: "unknown",
      headline: "Çalışma tamamlandı.",
      detail: "Bu çalışmanın ne ürettiği şu anda okunamadı. Sayfayı yenileyip tekrar bakabilirsiniz.",
      tone: "neutral",
    };
  }
  if (model.has_model) {
    return {
      key: "model",
      headline: "3D modeliniz hazır.",
      detail: "Yüzey modeli oluşturuldu ve görüntülenebilir.",
      tone: "success",
    };
  }
  if (model.dense.state === "ready") {
    return {
      key: "dense",
      headline: "3D yapı çıkarıldı.",
      detail:
        "Ayrıntılı nokta bulutu üretildi; bu bir yüzey modeli değildir. Yüzey modeli ayrı bir çalışmadır.",
      tone: "success",
    };
  }
  if (model.sparse.state === "ready") {
    return {
      key: "sparse",
      headline: "Kamera konumları çözüldü.",
      detail:
        "Çekiminizin 3D kontrolü tamamlandı: fotoğraflar birbiriyle eşleşti ve konumları bulundu. Bu bir yüzey modeli değildir.",
      tone: "success",
    };
  }
  return {
    key: "none",
    headline: "Bu çalışma geometri üretmedi.",
    detail: "Görüntülenebilecek bir sonuç oluşmadı.",
    tone: "caution",
  };
}

/* --- failures -------------------------------------------------------------- */

/**
 * Three kinds of failure, because they ask for three different things.
 *
 * `user_action` is fixed by capturing differently and a blind re-run will
 * reproduce it exactly. `infrastructure` is ours, and telling the customer to
 * re-shoot a capture that was fine is the worst thing this screen can do.
 * `terminal` is neither: something did not work and there is no established
 * remedy, so nothing is promised.
 */
export type FailureClass = "user_action" | "infrastructure" | "terminal";

const FAILURE_CLASSES: Record<string, FailureClass> = {
  not_enough_input: "user_action",
  insufficient_input: "user_action",
  feature_extraction_failed: "user_action",
  feature_matching_failed: "user_action",
  sparse_reconstruction_failed: "user_action",
  dense_reconstruction_failed: "user_action",
  mesh_generation_failed: "user_action",
  input_transfer_failed: "infrastructure",
  output_transfer_failed: "infrastructure",
  glb_packaging_failed: "infrastructure",
  engine_unavailable: "infrastructure",
  dense_unavailable: "infrastructure",
  mesh_unavailable: "infrastructure",
  engine_error: "infrastructure",
  engine_timeout: "user_action",
  artifact_validation_failed: "terminal",
  cancelled: "terminal",
};

export function failureClassOf(code: string | null | undefined): FailureClass {
  // An unrecognised code is a code this build has not been taught. It gets the
  // class that promises least.
  return (code ? FAILURE_CLASSES[code] : undefined) ?? "terminal";
}

/** The help topic that actually covers a failure, or null when none does. */
const FAILURE_HELP: Record<string, string> = {
  feature_extraction_failed: "iyi-cekim",
  feature_matching_failed: "iyi-cekim",
  sparse_reconstruction_failed: "rekonstruksiyon-hatalari",
  dense_reconstruction_failed: "rekonstruksiyon-hatalari",
  mesh_generation_failed: "rekonstruksiyon-hatalari",
  not_enough_input: "fotograf-cekimi",
  insufficient_input: "fotograf-cekimi",
  engine_timeout: "rekonstruksiyon-hatalari",
};

export function failureHelpTopic(code: string | null | undefined): string | null {
  return (code ? FAILURE_HELP[code] : undefined) ?? null;
}
